import React, { useCallback, useEffect, useState } from "react";
import { getMyPlayers, getMySignups } from "../api";
import {
  EventSignUpParticipantResponse,
  EventSignupResponse,
  MySignupResponse,
  OpenPlayerResponse,
} from "../types";
import { useSearchParams } from "react-router-dom";
import RichText from "./RichText";

const useEventId = () => {
  const [searchParams] = useSearchParams();
  return searchParams.get("eventId");
};

const useIsTest = () => {
  const [searchParams] = useSearchParams();
  return searchParams.get("test") === "true";
};

const JWT_KEY = "magic-link-jwt";
const AUTH_CHANGED_EVENT = "auth-changed";

const formatDate = (date: Date | string, includeTime: boolean) => {
  const d = new Date(date);
  if (includeTime)
    return d.toLocaleString("sv-SE", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  return d.toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

function getEmailFromJwt(jwt: string): string | null {
  try {
    const payload = JSON.parse(atob(jwt.split(".")[1]));
    return payload.email ?? null;
  } catch {
    return null;
  }
}

// const STATUS_LABELS: Record<string, string> = {
//   SignedUp: "Anmäld",
//   ConfirmedByAdmin: "Bekräftad",
//   RejectedByAdmin: "Avvisad",
// };

const PlayerCard: React.FC<{ player: OpenPlayerResponse }> = ({ player }) => {
  const ongoingBadge = player.badges.find((b) => b.notAchievedPercentage > 0);
  const achievedBadges = player.badges.filter(
    (b) => b.notAchievedPercentage === 0,
  );

  return (
    <div className="event-body">
      {player.groups.length > 0 && (
        <div className="player-card__groups">
          <span className="player-card__groups-label">Träningstider</span>
          <ul className="player-card__groups-list">
            {player.groups.map((g, i) => (
              <li key={i}>
                {g.name}
                {g.monthAttendancePercentage != null && (
                  <span className="player-card__attendance">
                    {" "}
                    — {g.monthAttendancePercentage}% senaste 30 dagarna
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {player.recommendedEvents.length > 0 && (
        <div className="player-card__groups">
          <span className="player-card__groups-label">
            Rekommenderade aktiviteter
          </span>
          <ul className="player-card__groups-list">
            {player.recommendedEvents.map((e) => (
              <li key={e.eventId}>
                <a
                  className="player-card__event-link"
                  href={`/?eventId=${e.eventId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {e.eventName}
                </a>
                {e.description && (
                  <span className="player-card__event-description">
                    {" "}
                    — {e.description}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {(achievedBadges.length > 0 || ongoingBadge) && (
        <div className="player-card__badges-section">
          <span className="player-card__groups-label">Märken</span>
          {achievedBadges.length > 0 && (
            <div className="player-card__badge-list">
              {achievedBadges.map((b, i) => (
                <span key={i} className="player-card__badge-name">
                  {b.name}
                  {b.achievedAt && (
                    <span className="player-card__badge-date">
                      {" "}
                      ({new Date(b.achievedAt).toLocaleDateString("sv-SE")})
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
          {ongoingBadge && (
            <div className="player-badge--ongoing">
              <div className="player-badge__name">{ongoingBadge.name}</div>
              <div className="player-badge__progress-bar">
                <div
                  className="player-badge__progress-fill"
                  style={{
                    width: `${Math.round(100 - ongoingBadge.notAchievedPercentage)}%`,
                  }}
                />
              </div>
              <div className="player-badge__status">
                {Math.round(100 - ongoingBadge.notAchievedPercentage)}% uppnått
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PlayerItem: React.FC<{
  player: OpenPlayerResponse;
  open: boolean;
  onToggle: () => void;
}> = ({ player, open, onToggle }) => (
  <div>
    <div className="section-header" onClick={onToggle}>
      <span className="section-header__arrow">{open ? "▼" : "▶"}</span>
      <h3 className="section-header__title">
        {player.firstName} {player.lastName}
        {player.birthYear != null && ` (${player.birthYear})`}
      </h3>
      <div className="section-header__divider" />
    </div>
    {open && <PlayerCard player={player} />}
  </div>
);

const ParticipantCard: React.FC<{
  participant: EventSignUpParticipantResponse;
}> = ({ participant }) => {
  // const status = STATUS_LABELS[participant.status] ?? "";
  const filledAlternatives = (participant.alternatives ?? []).filter(
    (a) => a.alternatives.length > 0,
  );
  const filledExtraFields = (participant.extraFields ?? []).filter(
    (f) => f.value,
  );

  return (
    <div className="participant-card">
      <div className="participant-card__name">
        {participant.firstName} {participant.lastName}
        {participant.birthYear != null && ` (${participant.birthYear})`}
      </div>
      <div className="participant-card__meta">
        {/* {status && <span>{status}</span>} */}
        {/* {participant.havePaid && <span>Betalat</span>} */}
        <span>
          Anmäld {new Date(participant.createdAt).toLocaleDateString("sv-SE")}
        </span>
      </div>
      {/* <div className="participant-card__field">
        <span className="participant-card__field-label">E-post:</span>
        {participant.contactEmail}
      </div> */}
      {/* <div className="participant-card__field">
        <span className="participant-card__field-label">Villkor:</span>
        {participant.haveAgreedConditions ? "Godkänt" : "Ej godkänt"}
      </div> */}
      {/* <div className="participant-card__field">
        <span className="participant-card__field-label">Lagring:</span>
        {participant.haveAgreedStorage ? "Godkänt" : "Ej godkänt"}
      </div> */}
      {filledAlternatives.map((alt, i) => (
        <div key={i} className="participant-card__field">
          <span className="participant-card__field-label">
            {alt.alternativeName}:
          </span>
          {alt.alternatives.join(", ")}
        </div>
      ))}
      {filledExtraFields.map((field, i) => (
        <div key={i} className="participant-card__field">
          <span className="participant-card__field-label">{field.name}:</span>
          {field.value}
        </div>
      ))}
    </div>
  );
};

const EventInfoCard: React.FC<{ event: EventSignupResponse }> = ({ event }) => (
  <div className="event-info-card">
    <RichText className="event-info-card__description rich-text">
      {event.description}
    </RichText>
    <div className="event-info-card__rows">
      <div className="event-info-card__row">
        <span className="event-info-card__label">Datum</span>
        <span>
          {formatDate(event.startDate, event.displayTimeStartEndDate)}
          {" – "}
          {formatDate(event.endDate, event.displayTimeStartEndDate)}
        </span>
      </div>
      {event.location && (
        <div className="event-info-card__row">
          <span className="event-info-card__label">Plats</span>
          <span>{event.location}</span>
        </div>
      )}
      {event.targetGroup && (
        <div className="event-info-card__row">
          <span className="event-info-card__label">Målgrupp</span>
          <span>{event.targetGroup}</span>
        </div>
      )}
      {event.costs && (
        <div className="event-info-card__row">
          <span className="event-info-card__label">Kostnad</span>
          <span>{event.costs}</span>
        </div>
      )}
      {event.conditions && (
        <div className="event-info-card__row">
          <span className="event-info-card__label">Villkor</span>
          <span>{event.conditions}</span>
        </div>
      )}
      {event.extraInfos?.map((info) => (
        <div key={info.id} className="event-info-card__row">
          <span className="event-info-card__label">{info.label}</span>
          <span>{info.text}</span>
        </div>
      ))}
      {event.alternatives
        ?.filter((alt) => alt.showInInfo)
        .map((alt, i) => (
          <div key={i} className="event-info-card__row">
            <span className="event-info-card__label">
              {alt.alternativeName}
            </span>
            <span>
              {alt.alternativeText}
              {alt.alternatives.length > 0 && (
                <ul className="event-info-card__list">
                  {alt.alternatives.map((option, idx) => (
                    <li key={idx}>{option}</li>
                  ))}
                </ul>
              )}
            </span>
          </div>
        ))}
      {/* {event.extraFields && event.extraFields.length > 0 && (
        <div className="event-info-card__row">
          <span className="event-info-card__label">Mer information</span>
          <span>
            {event.extraFields
              .map((f) => `${f.name}${f.isRequired ? "*" : ""}`)
              .join(", ")}
          </span>
        </div>
      )} */}
    </div>
  </div>
);

const EventItem: React.FC<{
  signup: MySignupResponse;
  open: boolean;
  onToggle: () => void;
}> = ({ signup, open, onToggle }) => (
  <div>
    <div className="section-header" onClick={onToggle}>
      <span className="section-header__arrow">{open ? "▼" : "▶"}</span>
      <h3 className="section-header__title">{signup.event.eventName}</h3>
      <div className="section-header__divider" />
    </div>
    {open && (
      <div className="event-body">
        <EventInfoCard event={signup.event} />
        <h4 className="participants-heading">Spelare du anmält</h4>
        <div className="participant-cards">
          {signup.participants.map((p) => (
            <ParticipantCard key={p.id} participant={p} />
          ))}
        </div>
      </div>
    )}
  </div>
);

const StartPage: React.FC = () => {
  const eventId = useEventId();
  const isTest = useIsTest();
  const [mySignups, setMySignups] = useState<MySignupResponse[]>([]);
  const [myPlayers, setMyPlayers] = useState<OpenPlayerResponse[]>([]);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [openPlayers, setOpenPlayers] = useState<Record<string, boolean>>({});
  const [listOpen, setListOpen] = useState(true);
  const [playersOpen, setPlayersOpen] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() =>
    Boolean(localStorage.getItem(JWT_KEY)),
  );

  useEffect(() => {
    const updateIsLoggedIn = () =>
      setIsLoggedIn(Boolean(localStorage.getItem(JWT_KEY)));
    const handleStorage = (e: StorageEvent) => {
      if (e.key === JWT_KEY) setIsLoggedIn(Boolean(e.newValue));
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener(AUTH_CHANGED_EVENT, updateIsLoggedIn);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(AUTH_CHANGED_EVENT, updateIsLoggedIn);
    };
  }, []);

  const fetchMySignups = useCallback(async () => {
    if (!isLoggedIn) {
      setMySignups([]);
      return;
    }
    try {
      const signups = await getMySignups(isTest);
      setMySignups(signups);
    } catch (error) {
      console.error("Error fetching my signups:", error);
    }
  }, [isLoggedIn, isTest]);

  const fetchMyPlayers = useCallback(async () => {
    if (!isLoggedIn) {
      setMyPlayers([]);
      return;
    }
    try {
      const players = await getMyPlayers(isTest);
      setMyPlayers(players);
    } catch (error) {
      console.error("Error fetching my players:", error);
    }
  }, [isLoggedIn, isTest]);

  useEffect(() => {
    fetchMySignups();
    fetchMyPlayers();
  }, [fetchMySignups, fetchMyPlayers]);

  const filteredSignups = (
    eventId
      ? mySignups.filter((s) => String(s.event.id) === eventId)
      : mySignups
  ).sort(
    (a, b) =>
      new Date(a.event.startDate).getTime() -
      new Date(b.event.startDate).getTime(),
  );

  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  const isGroupOpen = (key: string) => openGroups[key] !== false;

  const togglePlayer = (key: string) =>
    setOpenPlayers((prev) => ({ ...prev, [key]: !prev[key] }));
  const isPlayerOpen = (key: string) => openPlayers[key] !== false;

  const loggedInEmail = getEmailFromJwt(localStorage.getItem(JWT_KEY) ?? "");

  const hasContent = filteredSignups.length > 0 || myPlayers.length > 0;

  return (
    <>
      {hasContent && (
        <div className="container">
          {loggedInEmail && <h1>Hej {loggedInEmail}!</h1>}

          {myPlayers.length === 1 && (
            <PlayerItem
              key={myPlayers[0].playerId}
              player={myPlayers[0]}
              open={isPlayerOpen(myPlayers[0].playerId)}
              onToggle={() => togglePlayer(myPlayers[0].playerId)}
            />
          )}

          {myPlayers.length > 1 && (
            <>
              <div
                className="section-header"
                onClick={() => setPlayersOpen((o) => !o)}
              >
                <span className="section-header__arrow">
                  {playersOpen ? "▼" : "▶"}
                </span>
                <h2 className="section-header__title">Mina spelare</h2>
                <div className="section-header__divider" />
              </div>

              {playersOpen && (
                <div className="event-list">
                  {myPlayers.map((player) => (
                    <PlayerItem
                      key={player.playerId}
                      player={player}
                      open={isPlayerOpen(player.playerId)}
                      onToggle={() => togglePlayer(player.playerId)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {filteredSignups.length > 0 && (
            <>
              <div
                className="section-header"
                onClick={() => setListOpen((o) => !o)}
              >
                <span className="section-header__arrow">
                  {listOpen ? "▼" : "▶"}
                </span>
                <h2 className="section-header__title">Tävlingar & event</h2>
                <div className="section-header__divider" />
              </div>

              {listOpen && (
                <div className="event-list">
                  {filteredSignups.map((signup) => {
                    const key = String(signup.event.id);
                    return (
                      <EventItem
                        key={key}
                        signup={signup}
                        open={isGroupOpen(key)}
                        onToggle={() => toggleGroup(key)}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default StartPage;
